import React, { useState } from "react";
import { Icon } from "@iconify/react";
import {
  changeGiveawayStatus,
  pauseGiveaway,
  retryGiveawayEmails,
  deleteGiveawayTests,
} from "../services/giveawayService";
import {
  apiProblem,
  fmtEt,
  MOVE_LABELS,
  PHASE_LABELS,
  PURPLE,
  showProblem,
  STATUS_LABELS,
  STATUS_MOVES,
  toastOk,
} from "./giveawayUtils";
import { ActionModal, PlainList, SectionCard, StatusPill, Tile } from "./GiveawayUi";

const MOVE_HELP = {
  scheduled:
    "The page shows a coming soon message until the opening time, then opens for entries by itself.",
  active: "The page opens for entries right away, even if the opening time has not come yet.",
  draft: "The page goes back to being hidden from the public. Settings can be changed freely.",
  closed: "No more entries are accepted. You can then draw a winner from the Draw and winner tab.",
  archived: "The giveaway is put away. Its page stays hidden and nothing more can be done with it.",
};

const GiveawayOverviewTab = ({ g, reload, onUnauthorized, goTo }) => {
  const [modal, setModal] = useState(null);
  const counts = g.counts || {};
  const blockers = Array.isArray(g.launchBlockers) ? g.launchBlockers : [];
  const preLaunch = g.status === "draft" || g.status === "scheduled";
  const moves = STATUS_MOVES[g.status] || [];
  const canPause = g.status === "active" || g.status === "scheduled";
  const closesInFuture = g.closesAt && new Date(g.closesAt).getTime() > Date.now();

  const run = async (fn, okText, problemTitle) => {
    try {
      await fn();
      toastOk(okText);
      await reload();
      return true;
    } catch (e) {
      if (e?.response?.status === 401) {
        onUnauthorized();
        return true;
      }
      const p = apiProblem(e);
      await showProblem(p, problemTitle);
      if (p.list.length) reload();
      return false;
    }
  };

  const openMove = (to) => {
    const early = to === "closed" && closesInFuture;
    const launching = to === "scheduled" || to === "active";
    setModal({
      title: `${MOVE_LABELS[to]}?`,
      body: (
        <>
          <p className="mb-8">{MOVE_HELP[to]}</p>
          {early ? (
            <p className="mb-0">
              The closing time ({g.closesLabel || fmtEt(g.closesAt)}) has not come yet, so a reason is
              needed. It is kept in the audit trail.
            </p>
          ) : null}
          {launching && blockers.length ? (
            <p className="mb-0" style={{ color: "#FA69B9" }}>
              The launch checklist is not clear yet, so this will probably be refused.
            </p>
          ) : null}
        </>
      ),
      confirmLabel: MOVE_LABELS[to],
      danger: to === "archived" || early,
      fields: [
        {
          name: "reason",
          label: early ? "Why are you closing early?" : "Reason (optional)",
          type: "textarea",
          required: early,
          minLength: early ? 10 : undefined,
        },
      ],
      onConfirm: (v) =>
        run(
          () => changeGiveawayStatus(g._id, to, v.reason?.trim() || undefined),
          `Now ${(STATUS_LABELS[to] || to).toLowerCase()}`,
          launching ? "Not ready to launch" : undefined
        ),
    });
  };

  const openPause = () => {
    const pausing = !g.paused;
    setModal({
      title: pausing ? "Pause entries?" : "Resume entries?",
      body: pausing
        ? "The page stays up but says entries are paused. Nobody can enter until you resume."
        : "The page starts accepting entries again (if it is inside the open dates).",
      confirmLabel: pausing ? "Pause" : "Resume",
      fields: [{ name: "reason", label: "Reason", type: "textarea", required: true }],
      onConfirm: (v) =>
        run(() => pauseGiveaway(g._id, pausing, v.reason.trim()), pausing ? "Entries paused" : "Entries resumed"),
    });
  };

  const openRetry = () =>
    setModal({
      title: "Resend failed confirmation emails?",
      body: `${counts.confirmationEmailFailed} entrant${
        counts.confirmationEmailFailed === 1 ? "" : "s"
      } did not get their confirmation email. This tries again for each of them. These are real people.`,
      confirmLabel: "Resend",
      onConfirm: () => run(() => retryGiveawayEmails(g._id), "Emails sent again"),
    });

  const openDeleteTests = () =>
    setModal({
      title: "Delete test entries?",
      body: `This deletes the ${counts.tests} test entr${
        counts.tests === 1 ? "y" : "ies"
      } made through the preview link. Real entries are not touched.`,
      confirmLabel: "Delete test entries",
      danger: true,
      onConfirm: () => run(() => deleteGiveawayTests(g._id), "Test entries deleted"),
    });

  const copyPreview = async () => {
    try {
      await navigator.clipboard.writeText(g.previewUrl);
      toastOk("Preview link copied");
    } catch {
      window.prompt("Copy the preview link:", g.previewUrl);
    }
  };

  const contest = g.attribution?.contest || {};
  const acq = g.attribution?.acquisition || {};
  const hits = acq.smartLinkHits || {};
  const est = acq.estimatedRegistrations || {};
  const notes = Array.isArray(acq.notes) ? acq.notes : [];

  return (
    <div>
      {/* Status, dates, actions */}
      <SectionCard
        title="Status"
        subtitle={PHASE_LABELS[g.phase] || g.phase}
        actions={<StatusPill status={g.status} paused={g.paused} />}
      >
        <div className="row g-3 mb-16">
          <div className="col-md-4">
            <div className="text-sm text-secondary-light">Opens</div>
            <div className="fw-semibold">{g.opensLabel || fmtEt(g.opensAt)}</div>
          </div>
          <div className="col-md-4">
            <div className="text-sm text-secondary-light">Closes</div>
            <div className="fw-semibold">{g.closesLabel || fmtEt(g.closesAt)}</div>
          </div>
          <div className="col-md-4">
            <div className="text-sm text-secondary-light">Draw date</div>
            <div className="fw-semibold">{g.drawDateText || "Not set"}</div>
          </div>
        </div>

        {preLaunch ? (
          blockers.length ? (
            <div
              className="p-16 radius-8 mb-16"
              style={{ border: "1.5px solid #FA69B9", background: "rgba(250, 105, 185, 0.08)" }}
            >
              <div className="d-flex align-items-center gap-2 mb-8">
                <Icon icon="mdi:alert-circle-outline" style={{ color: "#FA69B9", fontSize: 22 }} />
                <h6 className="fw-semibold mb-0">Not ready to launch</h6>
              </div>
              <p className="text-sm mb-8">
                These need to be sorted out before the giveaway can be scheduled or opened:
              </p>
              <PlainList items={blockers} icon="mdi:checkbox-blank-outline" />
              <button
                type="button"
                className="btn btn-outline-primary btn-sm mt-12"
                onClick={() => goTo("settings")}
              >
                Go to Settings
              </button>
            </div>
          ) : (
            <div
              className="p-16 radius-8 mb-16 d-flex align-items-center gap-2"
              style={{ border: `1.5px solid ${PURPLE}`, background: "rgba(140, 80, 253, 0.08)" }}
            >
              <Icon icon="mdi:check-circle-outline" style={{ color: PURPLE, fontSize: 22 }} />
              <span className="fw-semibold">Launch checklist clear. Ready to schedule or open.</span>
            </div>
          )
        ) : null}

        <div className="d-flex flex-wrap gap-2">
          {moves.map((to) => (
            <button
              key={to}
              type="button"
              className={`btn btn-sm ${
                to === "archived" ? "btn-outline-danger" : to === "active" || to === "scheduled" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => openMove(to)}
            >
              {MOVE_LABELS[to]}
            </button>
          ))}
          {canPause ? (
            <button type="button" className="btn btn-sm btn-outline-warning" onClick={openPause}>
              <Icon icon={g.paused ? "mdi:play" : "mdi:pause"} className="me-1" />
              {g.paused ? "Resume entries" : "Pause entries"}
            </button>
          ) : null}
          {!moves.length && !canPause ? (
            <span className="text-sm text-secondary-light">
              {g.status === "drawn"
                ? "A winner has been drawn. Follow the steps in the Draw and winner tab."
                : "No status changes are possible from here."}
            </span>
          ) : null}
        </div>
      </SectionCard>

      {/* Entry counts */}
      <SectionCard title="Entries">
        <div className="row g-3">
          <Tile label="Total" value={counts.total} icon="mdi:account-multiple-outline" />
          <Tile label="Valid" value={counts.valid} icon="mdi:check-decagram-outline" />
          <Tile label="Needs review" value={counts.pendingReview} icon="mdi:eye-outline" />
          <Tile label="Duplicates" value={counts.duplicate} icon="mdi:content-duplicate" />
          <Tile label="Disqualified" value={counts.disqualified} icon="mdi:cancel" />
          <Tile label="Said yes to news" value={counts.marketingOptIns} icon="mdi:email-check-outline" sub="Marketing opt-ins" />
        </div>
        {g.emailsEnabled && counts.confirmationEmailFailed > 0 ? (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-16 p-12 radius-8 border">
            <span className="text-sm">
              {counts.confirmationEmailFailed} confirmation email
              {counts.confirmationEmailFailed === 1 ? "" : "s"} could not be sent.
            </span>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={openRetry}>
              Resend failed emails
            </button>
          </div>
        ) : null}
      </SectionCard>

      {/* Preview link */}
      <SectionCard
        title="Preview link"
        subtitle="Use this to see and test the page before it is public. Entries made through this link are marked as tests and are never included in the draw."
      >
        {g.previewUrl ? (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <input className="form-control" readOnly value={g.previewUrl} style={{ flex: "1 1 320px" }} />
            <button type="button" className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={copyPreview}>
              <Icon icon="mdi:content-copy" /> Copy
            </button>
            <a href={g.previewUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">
              Open
            </a>
          </div>
        ) : (
          <div className="text-sm text-secondary-light">No preview link yet.</div>
        )}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-16">
          <span className="text-sm">
            Test entries: <strong>{counts.tests || 0}</strong>
            {counts.tests > 0 && preLaunch ? " (these must be deleted before launch)" : ""}
          </span>
          {counts.tests > 0 ? (
            <button type="button" className="btn btn-outline-danger btn-sm" onClick={openDeleteTests}>
              Delete test entries
            </button>
          ) : null}
        </div>
      </SectionCard>

      {/* Two separate panels: the contest itself, then what it did for the app */}
      <div className="row g-3">
        <div className="col-lg-6">
          <SectionCard title="Contest" subtitle="How the giveaway page itself is doing.">
            <div className="row g-3">
              <Tile cols="col-6" label="Page views" value={contest.pageViews} icon="mdi:eye-outline" />
              <Tile cols="col-6" label="Started the form" value={contest.entryStarts} icon="mdi:form-select" />
              <Tile cols="col-6" label="Entries completed" value={contest.entriesCompleted} icon="mdi:check-all" />
              <Tile cols="col-6" label="Bots blocked" value={contest.blockedBots} icon="mdi:robot-off-outline" />
            </div>
          </SectionCard>
        </div>
        <div className="col-lg-6">
          <SectionCard title="Acquisition" subtitle="What the giveaway did for Curiouzz sign-ups.">
            <div className="row g-3">
              <Tile cols="col-6" label="App store taps from the page" value={acq.storeTapsFromPage} icon="mdi:cellphone-arrow-down" />
              <Tile
                cols="col-6"
                label="Smart link hits"
                value={(hits.ios || 0) + (hits.android || 0) + (hits.other || 0)}
                icon="mdi:link-variant"
                sub={`iPhone ${hits.ios || 0}, Android ${hits.android || 0}${hits.other ? `, other ${hits.other}` : ""}`}
              />
              <Tile
                cols="col-6"
                label="New registrants entered"
                value={acq.newRegistrantsEntered || 0}
                icon="mdi:account-plus-outline"
                sub="Finished registering, Gatekeeper included, during the giveaway"
              />
              <Tile
                cols="col-6"
                label="Verified registrations"
                value={acq.verifiedRegistrations}
                icon="mdi:account-check-outline"
                sub="Entries tied to an account that finished onboarding"
              />
              <Tile
                cols="col-6"
                label="Estimate: same-email sign-ups"
                value={est.newAccountsSameEmail}
                icon="mdi:approximately-equal"
                sub={`Estimate only. ${est.completedOnboarding || 0} finished onboarding. ${
                  est.existingMembersWhoEntered || 0
                } were already members.`}
              />
            </div>
            {notes.length ? (
              <div className="mt-16 text-secondary-light">
                <PlainList items={notes} icon="mdi:information-outline" />
              </div>
            ) : null}
          </SectionCard>
        </div>
      </div>

      <ActionModal show={!!modal} {...(modal || {})} onClose={() => setModal(null)} />
    </div>
  );
};

export default GiveawayOverviewTab;
